// frontend/src/pages/GestionEquipos.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { equipoService } from '../services/onebaseService';
import EquipoModal from '../components/EquipoModal';
import Loading from '../components/Loading';
import './GestionEquipos.css';

const GestionEquipos = () => {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroActivo, setFiltroActivo] = useState('all'); // 'all', 'active', 'inactive'
  const [modalOpen, setModalOpen] = useState(false);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);

  useEffect(() => {
    cargarEquipos();
  }, [filtroActivo]);

  const cargarEquipos = async () => {
    setLoading(true);
    try {
      const params = {};

      if (filtroActivo === 'active') {
        params.is_active = true;
      } else if (filtroActivo === 'inactive') {
        params.is_active = false;
      }

      const response = await equipoService.getAll(params);
      setEquipos(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error cargando equipos:', error);
      toast.error('Error al cargar equipos');
    } finally {
      setLoading(false);
    }
  };

  const handleNuevoEquipo = () => {
    setEquipoSeleccionado(null);
    setModalOpen(true);
  };

  const handleEditarEquipo = async (equipoId) => {
    try {
      const response = await equipoService.getById(equipoId);
      setEquipoSeleccionado(response.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Error cargando equipo:', error);
      toast.error('Error al cargar equipo');
    }
  };

  const handleEliminarEquipo = async (equipoId, equipoNombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar el equipo "${equipoNombre}"?`)) {
      return;
    }

    try {
      await equipoService.delete(equipoId);
      toast.success('Equipo eliminado exitosamente');
      cargarEquipos();
    } catch (error) {
      console.error('Error eliminando equipo:', error);
      toast.error(error.response?.data?.error || 'Error al eliminar equipo');
    }
  };

  const handleSaveEquipo = () => {
    cargarEquipos();
    setModalOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEquipoSeleccionado(null);
  };

  const formatCosto = (costo) => {
    if (!costo) return '$0';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(costo);
  };

  const equiposFiltrados = equipos;

  return (
    <div className="gestion-equipos-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Gestión de Equipos</h1>
        </div>
        <button
          className="estado-btn"
          onClick={handleNuevoEquipo}
        >
          + Nuevo Equipo
        </button>
      </div>

      <div className="filtros-section">
        <div className="filtro-buttons">
          <button
            className={`filtro-btn ${filtroActivo === 'all' ? 'active' : ''}`}
            onClick={() => setFiltroActivo('all')}
          >
            Todos
          </button>
          <button
            className={`filtro-btn ${filtroActivo === 'active' ? 'active' : ''}`}
            onClick={() => setFiltroActivo('active')}
          >
            Activos
          </button>
          <button
            className={`filtro-btn ${filtroActivo === 'inactive' ? 'active' : ''}`}
            onClick={() => setFiltroActivo('inactive')}
          >
            Inactivos
          </button>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : equiposFiltrados.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No hay equipos</h3>
          <p>Crea tu primer equipo para comenzar a organizar cuadrillas de trabajo</p>
          <button className="btn-crear-primero" onClick={handleNuevoEquipo}>
            Crear Primer Equipo
          </button>
        </div>
      ) : (
        <div className="equipos-grid">
          {equiposFiltrados.map(equipo => (
            <div key={equipo.id} className="equipo-card">
              <div
                className="equipo-color-bar"
                style={{ backgroundColor: equipo.color }}
              ></div>

              <div className="equipo-card-content">
                <div className="equipo-header">
                  <h3 className="equipo-nombre">{equipo.nombre}</h3>
                  {!equipo.is_active && (
                    <span className="badge-inactivo">Inactivo</span>
                  )}
                </div>

                {equipo.descripcion && (
                  <p className="equipo-descripcion">{equipo.descripcion}</p>
                )}

                <div className="equipo-stats">
                  <div className="stat-item">
                    <span className="stat-icon">👤</span>
                    <span className="stat-value">{equipo.total_miembros || 0}</span>
                    <span className="stat-label">
                      {equipo.total_miembros === 1 ? 'Miembro' : 'Miembros'}
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-icon">💰</span>
                    <div className="stat-value-container">
                      <span className="stat-value-small">
                        {formatCosto(equipo.costo_total_equipo)}
                      </span>
                      <span className="stat-label">Costo Total</span>
                    </div>
                  </div>
                </div>

                <div className="equipo-actions">
                  <button
                    className="btn-action btn-editar"
                    onClick={() => handleEditarEquipo(equipo.id)}
                    title="Editar equipo"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    className="btn-action btn-eliminar"
                    onClick={() => handleEliminarEquipo(equipo.id, equipo.nombre)}
                    title="Eliminar equipo"
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <EquipoModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        equipo={equipoSeleccionado}
        onSave={handleSaveEquipo}
      />
    </div>
  );
};

export default GestionEquipos;
